import { trackUnreadEncounterMessage } from "@/composables/encounterMessageReadState";
import { playRealtimeAlertSound } from "@/composables/realtimeAlertSound";
import { signalAuthorizationTitleAttention } from "@/composables/authorizationTitleAttention";

const targets = new Map();
const homeCountedEncounters = new Set();
const AUTH_NOTICE_AUTO_DISMISS_MS = 5000;
let dismissTimer = null;
const adminRequestEchoes = new Set();
const ADMIN_REQUEST_ECHO_TTL_MS = 2 * 60 * 1000;

const numericId = value => Number(value?.id ?? value ?? 0);

const matchesAdminRequest = (entry, event) => {
  return event.action === "requested" && !event.has_message && !event.message_id
    && numericId(event.provider_id) === entry.providerId
    && numericId(event.scheme_id) === entry.schemeId
    && Number(event.item_count) === entry.itemCount
    && (!entry.encounterId || numericId(event.encounter_id) === entry.encounterId)
    && (!entry.version || Number(event.version) > entry.version);
};

// Hold a possible echo until the save response identifies the saved encounter.
export const beginAdminAuthorizationRequest = ({ store, encounter, itemCount, existing = true }) => {
  if (store.state.user?.type !== "hmo" || !itemCount) {
    return { finish() {}, cancel() {} };
  }

  const entry = {
    userId: numericId(store.state.user.id),
    userSlug: store.state.user.slug,
    providerId: numericId(encounter.provider_id),
    schemeId: numericId(encounter.scheme_id),
    encounterId: existing ? numericId(encounter.id) : 0,
    itemCount, version: Number(store.state.realtimeAuthEvent?.version || 0),
    ready: false, held: [], startedAt: Date.now(),
  };
  if (!entry.providerId || !entry.schemeId) return { finish() {}, cancel() {} };

  const remove = () => {
    adminRequestEchoes.delete(entry);
    window.clearTimeout(entry.timer);
  };
  const release = () => {
    remove();
    entry.held.splice(0).forEach(({ replay }) => replay());
  };
  entry.remove = remove;
  entry.timer = window.setTimeout(release, ADMIN_REQUEST_ECHO_TTL_MS);
  adminRequestEchoes.add(entry);

  return {
    cancel: release,
    finish(saved) {
      entry.encounterId = numericId(saved?.encounter_id || saved?.id);
      if (!entry.encounterId) return release();
      entry.ready = true;
      let suppressed = false;
      entry.held.splice(0).forEach(({ event, replay }) => {
        if (!suppressed && matchesSavedAdminRequest(entry, event)) {
          suppressed = true;
          remove();
        } else {
          replay();
        }
      });
    },
  };
};

const matchesSavedAdminRequest = (entry, event) => {
  if (!matchesAdminRequest(entry, event)) return false;
  const receivedAt = Date.parse(event.received_at);
  // The listener stamps receipt in this browser, avoiding backend clock/record drift.
  return Number.isFinite(receivedAt)
    && receivedAt >= entry.startedAt
    && receivedAt <= entry.startedAt + ADMIN_REQUEST_ECHO_TTL_MS;
};

const holdAdminRequestEcho = (args) => {
  for (const entry of adminRequestEchoes) {
    const user = args.store.state.user;
    if (entry.userId !== numericId(user?.id) || entry.userSlug !== user?.slug
      || !matchesAdminRequest(entry, args.event)) continue;
    if (entry.ready) {
      if (!matchesSavedAdminRequest(entry, args.event)) continue;
      entry.remove();
    } else {
      entry.held.push({ event: args.event, replay: () => coordinateAuthorizationSignal({ ...args, skipAdminEcho: true }) });
    }
    return true;
  }
  return false;
};

const currentScheme = user => {
  const schemes = Array.isArray(user?.schemes) ? user.schemes : [];
  return schemes.find(scheme => scheme?.curr) || schemes[0] || null;
};

const schemeForEvent = (user, event) => {
  const schemeId = numericId(event?.scheme_id);
  return (user?.schemes || []).find(scheme => numericId(scheme) === schemeId) || null;
};

const itemDescription = event => {
  const count = Number(event?.item_count || 0);
  return `${count} authorization item${count === 1 ? "" : "s"}`;
};

const providerMessage = event => {
  const provider = event?.provider_name || "A provider";
  const action = event?.action === "withdrawn" ? "withdrew" : "submitted";
  const message = event?.has_message ? " with a message" : "";
  return `${provider} ${action} ${itemDescription(event)}${message}.`;
};

const requestNotice = (event, fallback) => {
  return event?.has_message ? `${fallback} with a message.` : `${fallback}.`;
};

const activateScheme = (store, schemeId) => {
  const user = store.state.user;
  const schemes = Array.isArray(user?.schemes) ? user.schemes : [];
  const match = schemes.find(scheme => numericId(scheme) === numericId(schemeId));

  if (!match) return false;

  const nextUser = {
    ...user,
    schemes: schemes.map(scheme => ({
      ...scheme,
      curr: numericId(scheme) === numericId(schemeId),
    })),
  };

  store.commit("loadUser", nextUser);
  return true;
};

const clearDismissTimer = () => {
  if (dismissTimer) {
    window.clearTimeout(dismissTimer);
    dismissTimer = null;
  }
};

const showNotice = (store, event, options = {}) => {
  clearDismissTimer();

  const notice = {
    ...event,
    signal_action: event.action,
    message: options.message || event.message,
    action: options.action || null,
    action_label: options.actionLabel || null,
    persistent: !options.autoDismissMs,
  };

  store.commit("showRealtimeAuthNotice", notice);

  if (["hmo", "provider"].includes(store.state.user?.type)) {
    playRealtimeAlertSound(event);
  }

  if (options.autoDismissMs) {
    dismissTimer = window.setTimeout(() => {
      store.commit("dismissRealtimeAuthNotice", event.event_id);
      dismissTimer = null;
    }, options.autoDismissMs);
  }
};

const incrementHomeAuthorizationCount = event => {
  if (event?.action !== "requested") return;

  const homeTarget = targets.get("home");
  const encounterId = numericId(event?.encounter_id);
  const schemeId = numericId(event?.scheme_id);

  if (!homeTarget || !encounterId) return;

  const key = `${schemeId}:${encounterId}`;
  if (homeCountedEncounters.has(key)) return;

  if (homeTarget.incrementAuthorizationCount(event)) {
    homeCountedEncounters.add(key);
  }
};

export const registerAuthorizationRealtimeTarget = (name, adapter) => {
  targets.set(name, adapter);

  return () => {
    if (targets.get(name) === adapter) {
      targets.delete(name);
    }
  };
};

export const coordinateAuthorizationSignal = ({ store, router, user, event, skipAdminEcho = false }) => {
  const route = router.currentRoute.value;
  const isHmo = user?.type === "hmo";

  store.commit("receiveRealtimeAuthEvent", event);

  if (isHmo && !skipAdminEcho && holdAdminRequestEcho({ store, router, user, event })) {
    incrementHomeAuthorizationCount(event);
    return;
  }

  signalAuthorizationTitleAttention(event);

  if (event.message_id && (event.action === "message_added" || event.has_message)) {
    trackUnreadEncounterMessage(user, event);
  }

  if (isHmo) {
    incrementHomeAuthorizationCount(event);

    const authTarget = targets.get("auth");
    const inAuthWorkflow = route.name === "auth" || route.name === "authView";
    const activeSchemeId = numericId(authTarget?.context?.().schemeId || currentScheme(user));
    const eventSchemeId = numericId(event.scheme_id);

    if (eventSchemeId && activeSchemeId && eventSchemeId !== activeSchemeId) {
      const scheme = schemeForEvent(user, event);
      showNotice(store, event, {
        message: event.action === "message_added"
          ? `A new encounter message from ${scheme?.name || "another scheme"} is waiting.`
          : `New authorization from ${scheme?.name || "another scheme"} is pending.`,
        action: "switch-scheme",
        actionLabel: "Switch scheme",
      });
      return;
    }

    if (event.action === "message_added") {
      const context = authTarget?.context?.() || {};
      const matchesOpenEncounter = route.name === "auth"
        && numericId(context.encounterId) === numericId(event.encounter_id);

      if (matchesOpenEncounter && authTarget?.refreshMessages?.(event)) {
        showNotice(store, event, {
          message: "Provider added a message to this encounter.",
          autoDismissMs: AUTH_NOTICE_AUTO_DISMISS_MS,
        });
        return;
      }

      if (route.name === "auth" && authTarget && !context.encounterId) {
        if (context.hasSearch) {
          showNotice(store, event, {
            message: "New encounter message received.",
            autoDismissMs: AUTH_NOTICE_AUTO_DISMISS_MS,
          });
          return;
        }

        if (context.currentPage > 1) {
          showNotice(store, event, {
            message: "A new encounter message is available.",
            action: "reload-auth-home",
            actionLabel: "View",
          });
          return;
        }

        authTarget.refreshList(event, 1);
        showNotice(store, event, {
          message: "New encounter message received.",
          autoDismissMs: AUTH_NOTICE_AUTO_DISMISS_MS,
        });
        return;
      }

      showNotice(store, event, {
        message: `${event.provider_name || "A provider"} added an encounter message.`,
        action: "open-auth-message",
        actionLabel: "View",
      });
      return;
    }

    if (route.name === "auth" && authTarget) {
      const context = authTarget.context();

      if (context.encounterId) {
        const matchesOpenEncounter = numericId(context.encounterId) === numericId(event.encounter_id);

        if (matchesOpenEncounter) {
          authTarget.refreshDetail(event);
        }

        showNotice(store, event, {
          message: matchesOpenEncounter
            ? requestNotice(event, "Provider updated this request")
            : "New request waiting.",
          autoDismissMs: AUTH_NOTICE_AUTO_DISMISS_MS,
        });
        return;
      }

      if (context.hasSearch) {
        showNotice(store, event, {
          message: "New authorization request received.",
          autoDismissMs: AUTH_NOTICE_AUTO_DISMISS_MS,
        });
        return;
      }

      if (context.currentPage > 1) {
        showNotice(store, event, {
          message: "A new authorization request is available.",
          action: "reload-auth-home",
          actionLabel: "View",
        });
        return;
      }

      authTarget.refreshList(event, 1);
      showNotice(store, event, {
        message: requestNotice(event, "New authorization request received"),
        autoDismissMs: AUTH_NOTICE_AUTO_DISMISS_MS,
      });
      return;
    }

    if (inAuthWorkflow) {
      showNotice(store, event, {
        message: "New request waiting.",
        autoDismissMs: AUTH_NOTICE_AUTO_DISMISS_MS,
      });
      return;
    }

    showNotice(store, event, {
      message: providerMessage(event),
      action: "open-auth",
      actionLabel: "View",
    });
    return;
  }

  const encounterTarget = targets.get("encounter");
  const inEncounterWorkflow = route.path.startsWith("/care/encounter");

  if (event.action === "message_added") {
    const context = encounterTarget?.context?.() || {};
    const matchesOpenEncounter = route.name === "encounter"
      && numericId(context.encounterId) === numericId(event.encounter_id);

    if (matchesOpenEncounter && encounterTarget?.refreshMessages?.(event)) {
      showNotice(store, event, {
        message: "HMO added a message to this encounter.",
        autoDismissMs: AUTH_NOTICE_AUTO_DISMISS_MS,
      });
      return;
    }

    if (route.name === "encounter" && encounterTarget?.refreshIfSafe(event)) {
      showNotice(store, event, {
        message: "HMO added a message to an encounter.",
        autoDismissMs: AUTH_NOTICE_AUTO_DISMISS_MS,
      });
      return;
    }

    showNotice(store, event, {
      message: "HMO added a message to an encounter.",
      action: "open-encounter-message",
      actionLabel: "View",
      autoDismissMs: inEncounterWorkflow ? AUTH_NOTICE_AUTO_DISMISS_MS : null,
    });
    return;
  }

  if (route.name === "encounter" && encounterTarget?.refreshIfSafe(event)) {
    showNotice(store, event, {
      message: `HMO reviewed ${itemDescription(event)}.`,
      autoDismissMs: AUTH_NOTICE_AUTO_DISMISS_MS,
    });
    return;
  }

  showNotice(store, event, {
    message: `HMO reviewed ${itemDescription(event)}.`,
    action: "open-encounter",
    actionLabel: "View",
    autoDismissMs: inEncounterWorkflow ? AUTH_NOTICE_AUTO_DISMISS_MS : null,
  });
};

export const handleAuthorizationNoticeAction = async ({ store, router, notice }) => {
  if (!notice) return;

  clearDismissTimer();

  if (notice.action === "switch-scheme") {
    if (!activateScheme(store, notice.scheme_id)) return;

    const authTarget = targets.get("auth");
    const opensMessage = notice.signal_action === "message_added";
    store.commit("queueRealtimeAuthSchemeSwitch", notice.scheme_id);
    store.commit("dismissRealtimeAuthNotice", notice.event_id);

    if (router.currentRoute.value.name === "auth" && authTarget) {
      await authTarget.switchScheme(notice.scheme_id);
      store.commit("queueRealtimeAuthSchemeSwitch", null);

      if (opensMessage) {
        await router.push({
          name: "auth",
          query: { encounterId: notice.encounter_id, tab: "messages" },
        });
      }
      return;
    }

    router.push(opensMessage
      ? { name: "auth", query: { encounterId: notice.encounter_id, tab: "messages" } }
      : { name: "auth" });
    return;
  }

  store.commit("dismissRealtimeAuthNotice", notice.event_id);

  if (notice.action === "open-auth") {
    router.push({ name: "auth" });
  } else if (notice.action === "open-auth-message") {
    router.push({
      name: "auth",
      query: { encounterId: notice.encounter_id, tab: "messages" },
    });
  } else if (notice.action === "reload-auth-home") {
    window.location.assign(router.resolve({ name: "auth" }).href);
  } else if (notice.action === "open-encounter") {
    router.push({ name: "encounter" });
  } else if (notice.action === "open-encounter-message") {
    router.push({
      name: "encounter",
      hash: "#start",
      query: { messageEncounterId: notice.encounter_id },
    });
  }
};

export const reconcileAuthorizationHomeCount = () => {
  homeCountedEncounters.clear();
};

export const dismissPersistentAuthorizationNotice = store => {
  if (store.state.realtimeAuthNotice?.persistent) {
    dismissAuthorizationNotice(store);
  }
};

export const dismissAuthorizationNotice = (store, eventId = null) => {
  clearDismissTimer();
  store.commit("dismissRealtimeAuthNotice", eventId);
};
